import { Link } from "react-router";
import { cn } from "@veo/ui";
import { absoluteUrl } from "../lib/site";

export interface Crumb {
  label: string;
  href?: string;
}

/**
 * Breadcrumb trail.
 *
 * The current page is the last item and is not a link — `aria-current="page"`
 * marks it, and a link to where you already are is noise for everyone.
 *
 * The BreadcrumbList markup is emitted alongside, which is what makes Google
 * show "veo › Services › Retirement Planning" under a result rather than a raw
 * URL. It is generated from the same array the trail renders, so the two cannot
 * disagree.
 */
export function Breadcrumbs({
  items,
  onDark = false,
  className,
}: {
  items: Crumb[];
  onDark?: boolean;
  className?: string;
}) {
  if (items.length === 0) return null;

  const trail: Crumb[] = [{ label: "Home", href: "/" }, ...items];

  return (
    <>
      <nav aria-label="Breadcrumb" className={className}>
        <ol
          className={cn(
            "flex flex-wrap items-center gap-2 text-xs",
            onDark ? "text-navy-300" : "text-ink-500",
          )}
        >
          {trail.map((crumb, index) => {
            const isLast = index === trail.length - 1;

            return (
              <li key={`${crumb.label}-${index}`} className="flex items-center gap-2">
                {index > 0 && <span aria-hidden>/</span>}
                {crumb.href && !isLast ? (
                  <Link
                    to={crumb.href}
                    className={onDark ? "hover:text-cream-100" : "hover:text-navy-900"}
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current="page" className={onDark ? "text-gold-300" : "text-navy-800"}>
                    {crumb.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: trail.map((crumb, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: crumb.label,
              ...(crumb.href ? { item: absoluteUrl(crumb.href) } : {}),
            })),
          }).replace(/</g, "\\u003c"),
        }}
      />
    </>
  );
}
